import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { medicsAPI, shiftsAPI, assignmentAPI, jobTypesAPI, contractsAPI, leaveAPI, WARNING_TYPES, WARNING_ACTIONS } from '../lib/api';
import { fmt } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  User,
  Clock,
  Calendar,
  Users,
  Filter,
  X,
  CheckCircle,
  Copy,
  CalendarRange,
  CalendarDays,
  PoundSterling,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  addMonths, 
  subMonths, 
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { useAuth } from '../context/AuthContext';
import ComplianceWarningModal from '../components/ComplianceWarningModal';
import { 
  CreateShiftModal, 
  BulkCreateModal, 
  ScheduleCalendar, 
  CalendarLegend 
} from '../components/schedule';

export default function Schedule() {
  const { user, isAdmin } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  // iter72.8 — Schedule planners asked for maximum room for the week
  // view on laptop screens. Persist the KPI-collapse preference so
  // their choice sticks across sessions.
  const [statsCollapsed, setStatsCollapsed] = useState(() => {
    try { return localStorage.getItem('elara.schedule.statsCollapsed') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('elara.schedule.statsCollapsed', statsCollapsed ? '1' : '0'); }
    catch { /* quota — ignore */ }
    // Shrink the calendar's offset variable when stats are hidden so
    // the ``clamp()`` height calc recovers that vertical space.
    document.documentElement.style.setProperty(
      '--schedule-calendar-offset',
      statsCollapsed ? '240px' : '340px',
    );
    return () => document.documentElement.style.removeProperty('--schedule-calendar-offset');
  }, [statsCollapsed]);
  const [shifts, setShifts] = useState([]);
  const [medics, setMedics] = useState([]);
  const [approvedLeave, setApprovedLeave] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [draggedShift, setDraggedShift] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  
  // Expandable shift panel state
  const [expandedShiftId, setExpandedShiftId] = useState(null);
  
  // Bulk create state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkData, setBulkData] = useState({
    recurrence_pattern: 'weekly',
    specific_days: [],
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(addWeeks(new Date(), 4), 'yyyy-MM-dd'),
    start_time: '08:00',
    end_time: '20:00',
    location: '',
    address: '',
    postcode: '',
    shift_type: 'regular',
    job_type_id: '',
    contract_id: '',
    medic_id: '',
    rate_payable: 0,
    rate_chargeable: 0,
    notes: '',
  });
  
  // Filters
  const [filters, setFilters] = useState({
    medic_id: 'all',
    status: 'all',
    shift_type: 'all',
    job_type_id: 'all',
    location: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Compliance warning modal state
  const [showComplianceWarning, setShowComplianceWarning] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [currentWarning, setCurrentWarning] = useState(null);

  const [newShift, setNewShift] = useState({
    date: '',
    end_date: '',
    start_time: '08:00',
    end_time: '20:00',
    location: '',
    address: '',
    postcode: '',
    shift_type: 'regular',
    job_type_id: '',
    contract_id: '',
    medic_id: '',
    rate_payable: 0,
    rate_chargeable: 0,
    notes: '',
    long_duration_ack: false,
  });

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      return { start, end };
    } else {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      return { start, end };
    }
  }, [currentDate, viewMode]);

  const fetchData = useCallback(async () => {
    try {
      const start = format(dateRange.start, 'yyyy-MM-dd');
      const end = format(dateRange.end, 'yyyy-MM-dd');
      
      const [shiftsRes, medicsRes, jobTypesRes, contractsRes, leaveRes] = await Promise.all([
        shiftsAPI.list({ date_from: start, date_to: end }),
        medicsAPI.list(),
        jobTypesAPI.list(),
        contractsAPI.list(),
        leaveAPI.list({ status: 'approved', start_date: start, end_date: end }).catch(() => ({ data: [] })),
      ]);
      
      setShifts(shiftsRes.data);
      setMedics(medicsRes.data);
      setJobTypes(jobTypesRes.data);
      setContracts(contractsRes.data);
      setApprovedLeave(leaveRes.data);
    } catch (error) {
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Click-outside handler to close expanded shift card
  const calendarRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle clicks when a card is expanded
      if (!expandedShiftId) return;
      
      // Check if click is inside a shift card (has data-testid starting with shift-card-)
      const clickedShiftCard = event.target.closest('[data-testid^="shift-card-"]');
      if (clickedShiftCard) return;
      
      // Check if click is inside a select dropdown (Radix portals to body)
      const clickedSelect = event.target.closest('[data-radix-popper-content-wrapper]');
      if (clickedSelect) return;
      
      // Close the expanded card
      setExpandedShiftId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedShiftId]);

  // Filter shifts
  const filteredShifts = useMemo(() => {
    return shifts.filter(shift => {
      if (filters.medic_id !== 'all') {
        if (filters.medic_id === 'unassigned' && shift.medic_id) return false;
        if (filters.medic_id !== 'unassigned' && shift.medic_id !== filters.medic_id) return false;
      }
      if (filters.status !== 'all' && shift.status !== filters.status) return false;
      if (filters.shift_type !== 'all' && shift.shift_type !== filters.shift_type) return false;
      if (filters.job_type_id !== 'all' && shift.job_type_id !== filters.job_type_id) return false;
      if (filters.location && !shift.location?.toLowerCase().includes(filters.location.toLowerCase())) return false;
      return true;
    });
  }, [shifts, filters]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = filteredShifts.length;
    const assigned = filteredShifts.filter(s => s.medic_id).length;
    const unassigned = total - assigned;
    const confirmed = filteredShifts.filter(s => s.status === 'confirmed').length;
    const pending = filteredShifts.filter(s => s.status === 'pending').length;
    const totalPayable = filteredShifts.reduce((sum, s) => sum + (s.rate_payable || 0), 0);
    
    return { total, assigned, unassigned, confirmed, pending, totalPayable };
  }, [filteredShifts]);

  // Check for conflicts (multiple shifts same medic same time)
  const getShiftConflicts = useCallback((shift) => {
    if (!shift.medic_id) return [];
    
    return shifts.filter(s => 
      s.id !== shift.id &&
      s.medic_id === shift.medic_id &&
      s.date === shift.date &&
      s.status !== 'cancelled' &&
      ((s.start_time < shift.end_time && s.end_time > shift.start_time))
    );
  }, [shifts]);

  // Navigation handlers
  const handlePrev = () => {
    if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const handleToday = () => setCurrentDate(new Date());

  const handleDayClick = (date) => {
    setExpandedShiftId(null); // Close any expanded shift card when clicking a day
    setSelectedDate(format(date, 'yyyy-MM-dd'));
    setNewShift(prev => ({ ...prev, date: format(date, 'yyyy-MM-dd') }));
    setShowCreateModal(true);
  };

  const toggleShiftExpand = (shiftId, e) => {
    e?.stopPropagation();
    setExpandedShiftId(prev => prev === shiftId ? null : shiftId);
  };

  const closeExpandedShift = () => {
    setExpandedShiftId(null);
  };

  const handleCreateShift = async () => {
    try {
      // Iter63: Contract → Job Type → Shift chain is enforced on the
      // backend. Catch missing picks here so the admin gets a clear
      // toast rather than a 422 payload.
      if (!newShift.contract_id || !newShift.job_type_id) {
        toast.error('Please pick both a Contract and a Job Type before creating a shift.');
        return;
      }
      // Iter64: postcode is required so geocoding has a reliable anchor.
      if (!newShift.postcode) {
        toast.error('Please enter a postcode so the shift can be geofenced.');
        return;
      }

      // Only send end_date to the backend if it extends beyond the start
      // date — same-day shifts (and overnight shifts encoded as end_time <
      // start_time on the same calendar day) stay single-day records.
      const extendsBeyondStart = !!newShift.end_date && newShift.end_date > newShift.date;

      await shiftsAPI.create({
        ...newShift,
        end_date: extendsBeyondStart ? newShift.end_date : null,
        rate_payable: parseFloat(newShift.rate_payable) || 0,
        rate_chargeable: parseFloat(newShift.rate_chargeable) || 0,
        medic_id: newShift.medic_id || null,
        job_type_id: newShift.job_type_id,
        contract_id: newShift.contract_id,
        address: newShift.address || null,
        postcode: newShift.postcode || null,
      });
      toast.success(extendsBeyondStart ? 'Multi-day shift created' : 'Shift created successfully');

      setShowCreateModal(false);
      setNewShift({
        date: '',
        end_date: '',
        start_time: '08:00',
        end_time: '20:00',
        location: '',
        address: '',
        postcode: '',
        shift_type: 'regular',
        job_type_id: '',
        contract_id: '',
        medic_id: '',
        rate_payable: 0,
        rate_chargeable: 0,
        notes: '',
        long_duration_ack: false,
      });
      // Guard fetchData so a downstream render issue after a successful
      // save can't blank the page (iter64 P0 — the user hit this twice,
      // had to log out/back in). If the refresh fails the create still
      // persisted on the server; the next natural navigation will show
      // the new shift.
      try {
        await fetchData();
      } catch (refreshErr) {
        toast.error('Shift saved, but the schedule failed to refresh. Please reload the page.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    }
  };

  // Bulk create handlers
  const handleBulkPreview = async () => {
    if (!bulkData.location) {
      toast.error('Please enter a location');
      return;
    }
    if (!bulkData.contract_id || !bulkData.job_type_id) {
      toast.error('Please pick both a Contract and a Job Type for the bulk run.');
      return;
    }
    if (!bulkData.postcode) {
      toast.error('Please enter a postcode so the shifts can be geofenced.');
      return;
    }
    if (bulkData.recurrence_pattern === 'specific_days' && bulkData.specific_days.length === 0) {
      toast.error('Please select at least one day of the week');
      return;
    }
    
    setBulkLoading(true);
    try {
      const response = await shiftsAPI.previewRecurring({
        ...bulkData,
        rate_payable: parseFloat(bulkData.rate_payable) || 0,
        rate_chargeable: parseFloat(bulkData.rate_chargeable) || 0,
        job_type_id: bulkData.job_type_id,
        contract_id: bulkData.contract_id,
        address: bulkData.address || null,
        postcode: bulkData.postcode || null,
        medic_id: bulkData.medic_id || null,
      });
      setBulkPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview shifts');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    if (!bulkData.contract_id || !bulkData.job_type_id) {
      toast.error('Please pick both a Contract and a Job Type for the bulk run.');
      return;
    }
    if (!bulkData.postcode) {
      toast.error('Please enter a postcode so the shifts can be geofenced.');
      return;
    }
    setBulkLoading(true);
    try {
      const response = await shiftsAPI.createRecurring({
        ...bulkData,
        rate_payable: parseFloat(bulkData.rate_payable) || 0,
        rate_chargeable: parseFloat(bulkData.rate_chargeable) || 0,
        job_type_id: bulkData.job_type_id,
        contract_id: bulkData.contract_id,
        address: bulkData.address || null,
        postcode: bulkData.postcode || null,
        medic_id: bulkData.medic_id || null,
      });
      toast.success(response.data.message);
      setShowBulkModal(false);
      setBulkPreview(null);
      setBulkData({
        recurrence_pattern: 'weekly',
        specific_days: [],
        start_date: format(new Date(), 'yyyy-MM-dd'),
        end_date: format(addWeeks(new Date(), 4), 'yyyy-MM-dd'),
        start_time: '08:00',
        end_time: '20:00',
        location: '',
        shift_type: 'regular',
        job_type_id: '',
        contract_id: '',
        medic_id: '',
        rate_payable: 0,
        rate_chargeable: 0,
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shifts');
    } finally {
      setBulkLoading(false);
    }
  };

  // Log warning action to audit trail
  const logWarningAction = async (warningType, action, medicId, shiftId = null, jobTypeId = null) => {
    try {
      await assignmentAPI.logWarningAction({
        warning_type: warningType,
        action: action,
        medic_id: medicId,
        shift_id: shiftId,
        job_type_id: jobTypeId,
      });
    } catch (error) {
      console.error('Failed to log warning action:', error);
    }
  };

  // Check compliance before assigning medic
  const checkMedicCompliance = async (medicId, shiftId) => {
    if (isAdmin()) {
      return { canProceed: true, warnings: [] };
    }
    
    try {
      const response = await assignmentAPI.checkAssignment({ medic_id: medicId, shift_id: shiftId });
      const result = response.data;
      
      const warnings = [];
      
      if (result.compliance_status === 'expired') {
        warnings.push({ type: WARNING_TYPES.EXPIRED, jobTypeId: null, jobTypeName: null });
      } else if (result.compliance_status === 'expiring_soon') {
        warnings.push({ type: WARNING_TYPES.EXPIRING_SOON, jobTypeId: null, jobTypeName: null });
      }
      
      if (result.job_type_warning) {
        warnings.push({ type: WARNING_TYPES.JOB_TYPE_NOT_APPROVED, jobTypeId: result.job_type_id, jobTypeName: result.job_type_name });
      }
      
      return { canProceed: warnings.length === 0, warnings };
    } catch (error) {
      console.error('Error checking compliance:', error);
      return { canProceed: true, warnings: [] };
    }
  };

  const handleMedicAssignment = async (medicId, shift) => {
    if (!shift || !medicId) return;
    
    const medic = medics.find(m => m.id === medicId);
    
    // Check for shift conflicts (overlapping shifts for the same medic on the same day)
    const conflictingShifts = shifts.filter(s => 
      s.medic_id === medicId && 
      s.date === shift.date && 
      s.id !== shift.id &&
      // Check time overlap
      !(s.end_time <= shift.start_time || s.start_time >= shift.end_time)
    );
    
    if (conflictingShifts.length > 0) {
      const conflictInfo = conflictingShifts.map(s => 
        `${s.start_time}-${s.end_time} at ${s.location || 'Unknown location'}`
      ).join(', ');
      
      toast.warning(`Shift Conflict Detected`, {
        description: `${medic?.name} already has ${conflictingShifts.length} overlapping shift(s) on this date: ${conflictInfo}`,
        duration: 6000,
      });
      // Continue with assignment but show warning
    }
    
    const checkResult = await checkMedicCompliance(medicId, shift.id);
    
    if (!checkResult.canProceed && checkResult.warnings.length > 0) {
      const firstWarning = checkResult.warnings[0];
      
      await logWarningAction(firstWarning.type, WARNING_ACTIONS.VIEWED, medicId, shift.id, firstWarning.jobTypeId);
      
      setCurrentWarning({
        type: firstWarning.type,
        medicId: medicId,
        medicName: medic?.name,
        shiftId: shift.id,
        jobTypeId: firstWarning.jobTypeId,
        jobTypeName: firstWarning.jobTypeName,
        remainingWarnings: checkResult.warnings.slice(1),
        hasConflict: conflictingShifts.length > 0,
        conflictCount: conflictingShifts.length,
      });
      setPendingAssignment({ medicId, shift });
      setShowComplianceWarning(true);
      return;
    }
    
    await handleUpdateShift(shift.id, { medic_id: medicId });
  };

  const handleConfirmComplianceWarning = async () => {
    if (!pendingAssignment || !currentWarning) return;
    
    await logWarningAction(currentWarning.type, WARNING_ACTIONS.ACKNOWLEDGED, currentWarning.medicId, currentWarning.shiftId, currentWarning.jobTypeId);
    
    if (currentWarning.remainingWarnings && currentWarning.remainingWarnings.length > 0) {
      const nextWarning = currentWarning.remainingWarnings[0];
      await logWarningAction(nextWarning.type, WARNING_ACTIONS.VIEWED, currentWarning.medicId, currentWarning.shiftId, nextWarning.jobTypeId);
      
      setCurrentWarning({
        ...currentWarning,
        type: nextWarning.type,
        jobTypeId: nextWarning.jobTypeId,
        jobTypeName: nextWarning.jobTypeName,
        remainingWarnings: currentWarning.remainingWarnings.slice(1),
      });
      return;
    }
    
    setShowComplianceWarning(false);
    await handleUpdateShift(pendingAssignment.shift.id, { medic_id: pendingAssignment.medicId });
    setPendingAssignment(null);
    setCurrentWarning(null);
  };

  const handleCancelComplianceWarning = async () => {
    if (currentWarning) {
      await logWarningAction(currentWarning.type, WARNING_ACTIONS.CANCELLED, currentWarning.medicId, null, currentWarning.jobTypeId);
    }
    
    setShowComplianceWarning(false);
    setPendingAssignment(null);
    setCurrentWarning(null);
  };

  const handleUpdateShift = async (shiftId, updates) => {
    try {
      await shiftsAPI.update(shiftId, updates);
      toast.success('Shift updated successfully');
      setExpandedShiftId(null); // Close expanded card after update
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return;

    // Optimistically hide the shift and offer a 10s undo before the backend call.
    setShifts(prev => prev.filter(s => s.id !== shiftId));
    setExpandedShiftId(null);

    let undone = false;
    const commit = async () => {
      if (undone) return;
      try {
        await shiftsAPI.delete(shiftId);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete shift');
        fetchData(); // restore state from server
      }
    };

    const timer = setTimeout(commit, 10000);

    toast('Shift deleted', {
      description: `${shift.date} · ${shift.location || 'no location'}`,
      duration: 10000,
      action: {
        label: 'Undo',
        onClick: () => {
          undone = true;
          clearTimeout(timer);
          setShifts(prev => prev.some(s => s.id === shiftId) ? prev : [...prev, shift]);
          toast.success('Deletion undone');
        },
      },
    });
  };

  const handleMarkNoShow = async (shiftId) => {
    if (!window.confirm('Mark this shift as no-show? The medic\'s payable rate will be zeroed for reporting.')) return;
    try {
      await shiftsAPI.markNoShow(shiftId);
      toast.success('Shift marked as no-show');
      setExpandedShiftId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark no-show');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, shift) => {
    setDraggedShift(shift);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', shift.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    if (!draggedShift) return;
    
    const newDate = format(targetDate, 'yyyy-MM-dd');
    if (newDate === draggedShift.date) {
      setDraggedShift(null);
      setExpandedShiftId(null); // Close expanded card after drag
      return;
    }
    
    try {
      await shiftsAPI.update(draggedShift.id, { date: newDate });
      toast.success('Shift moved to ' + format(targetDate, 'MMM d'));
      setExpandedShiftId(null); // Close expanded card after successful move
      fetchData();
    } catch (error) {
      toast.error('Failed to move shift');
    }
    setDraggedShift(null);
  };

  // Handle dropping shift on a medic for reassignment
  const handleDropOnMedic = async (e, medicId, shift) => {
    e.preventDefault();
    e.stopPropagation();
    if (shift.medic_id === medicId) return;
    
    await handleMedicAssignment(medicId, shift);
  };

  const clearFilters = () => {
    setFilters({ medic_id: 'all', status: 'all', shift_type: 'all', job_type_id: 'all', location: '' });
  };

  const hasActiveFilters = filters.medic_id !== 'all' || filters.status !== 'all' || filters.shift_type !== 'all' || filters.job_type_id !== 'all' || filters.location !== '';

  // Out-of-range matches: when a filter is active and the current week/month
  // has no matching shifts, we probe the backend without a date range so we
  // can tell the dispatcher "0 here, N elsewhere" and let them jump to the
  // nearest match instead of staring at an empty grid.
  const [outOfRangeMatches, setOutOfRangeMatches] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Reset dismissal whenever filter criteria change so the banner can show again.
  useEffect(() => { setBannerDismissed(false); }, [filters.medic_id, filters.status, filters.shift_type, filters.job_type_id, filters.location]);

  useEffect(() => {
    // Only probe when a filter is active and nothing matches in the current window.
    if (!hasActiveFilters || filteredShifts.length > 0) {
      setOutOfRangeMatches([]);
      return;
    }
    let cancelled = false;
    const params = {};
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.shift_type !== 'all') params.shift_type = filters.shift_type;
    if (filters.job_type_id !== 'all') params.job_type_id = filters.job_type_id;
    // medic_id / location aren't server params on list_shifts — we filter them client-side below.
    shiftsAPI.list(params).then(res => {
      if (cancelled) return;
      const rangeStart = format(dateRange.start, 'yyyy-MM-dd');
      const rangeEnd = format(dateRange.end, 'yyyy-MM-dd');
      const matches = (res.data || []).filter(s => {
        // Must fall outside the currently displayed window.
        const outside = s.date < rangeStart || s.date > rangeEnd;
        if (!outside) return false;
        // Apply medic + location filters client-side (parity with filteredShifts).
        if (filters.medic_id === 'unassigned' && s.medic_id) return false;
        if (filters.medic_id !== 'all' && filters.medic_id !== 'unassigned' && s.medic_id !== filters.medic_id) return false;
        if (filters.location && !s.location?.toLowerCase().includes(filters.location.toLowerCase())) return false;
        return true;
      });
      setOutOfRangeMatches(matches);
    }).catch(() => {
      if (!cancelled) setOutOfRangeMatches([]);
    });
    return () => { cancelled = true; };
  }, [hasActiveFilters, filteredShifts.length, filters, dateRange]);

  const jumpToEarliestMatch = () => {
    if (outOfRangeMatches.length === 0) return;
    const sorted = [...outOfRangeMatches].sort((a, b) => a.date.localeCompare(b.date));
    const target = new Date(`${sorted[0].date}T00:00:00`);
    setCurrentDate(target);
  };

  // Generate days for the view
  const days = useMemo(() => {
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  }, [dateRange]);

  // For month view, we need padding
  const paddedDays = useMemo(() => {
    if (viewMode === 'month') {
      const startPadding = dateRange.start.getDay();
      return [...Array(startPadding).fill(null), ...days];
    }
    return days;
  }, [viewMode, dateRange.start, days]);

  const getShiftsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const results = [];
    for (const s of filteredShifts) {
      if (!s.is_multi_day || !s.end_date) {
        // Single-day shift — simple match.
        if (s.date === dateStr) results.push(s);
        continue;
      }
      // Multi-day shift — render on every covered day with a segment tag
      // so the ShiftCard can show "Day X of Y / Start / End" labels.
      if (dateStr < s.date || dateStr > s.end_date) continue;
      const spanStart = new Date(`${s.date}T00:00:00`);
      const spanEnd = new Date(`${s.end_date}T00:00:00`);
      const totalDays = Math.round((spanEnd - spanStart) / (1000 * 60 * 60 * 24)) + 1;
      const dayIndex = Math.round((date - spanStart) / (1000 * 60 * 60 * 24)) + 1;
      let segment = 'middle';
      if (dateStr === s.date) segment = 'start';
      else if (dateStr === s.end_date) segment = 'end';
      results.push({ ...s, _multiDaySegment: segment, _dayIndex: dayIndex, _dayTotal: totalDays });
    }
    return results;
  };

  const getLeaveForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return approvedLeave.filter(l => l.start_date <= dateStr && l.end_date >= dateStr);
  };

  // Get medic by ID helper
  const getMedicById = (medicId) => medics.find(m => m.id === medicId);

  // Get display title based on view mode
  const getDisplayTitle = () => {
    if (viewMode === 'week') {
      const start = dateRange.start;
      const end = dateRange.end;
      if (start.getMonth() === end.getMonth()) {
        return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
      }
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'MMMM yyyy');
  };

  // Split title into range + year so we can style the year as a lighter
  // accent — gives the header a cleaner, editorial feel.
  const getTitleParts = () => {
    if (viewMode === 'week') {
      const start = dateRange.start;
      const end = dateRange.end;
      const range = start.getMonth() === end.getMonth()
        ? `${format(start, 'MMM d')} – ${format(end, 'd')}`
        : `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
      return { range, year: format(end, 'yyyy') };
    }
    return { range: format(currentDate, 'MMMM'), year: format(currentDate, 'yyyy') };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-5 flex flex-col lg:min-h-[calc(100vh-7.5rem)]" data-testid="schedule-container">
      {/* iter72.8 — KPI row is collapsible on desktop so planners can
          dedicate the viewport to the calendar. Mobile keeps the old
          layout untouched (``lg:`` prefix on the toggle). */}
      <div className="flex items-center justify-between -mb-1 hidden lg:flex">
        <span className="text-xs text-[#A1A1AA] uppercase tracking-wide">Overview</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setStatsCollapsed(v => !v)}
          className="h-7 text-xs text-[#A1A1AA] hover:text-white hover:bg-white/5"
          data-testid="schedule-stats-toggle"
        >
          {statsCollapsed ? <><ChevronDown className="w-3.5 h-3.5 mr-1" />Show metrics</> : <><ChevronUp className="w-3.5 h-3.5 mr-1" />Hide metrics</>}
        </Button>
      </div>
      {/* Stats Cards — hidden on desktop when collapsed; always shown on mobile. */}
      <div className={`grid grid-cols-2 lg:grid-cols-6 gap-4 ${statsCollapsed ? 'lg:hidden' : ''}`}>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <Calendar className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Total Shifts</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <Users className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Assigned</p>
                <p className="text-xl font-bold text-[#1ABC9C] font-['JetBrains_Mono']">{stats.assigned}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                <User className="w-5 h-5 text-[#FFC04D]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Unassigned</p>
                <p className="text-xl font-bold text-[#FFC04D] font-['JetBrains_Mono']">{stats.unassigned}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <CheckCircle className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Confirmed</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">{stats.confirmed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                <Clock className="w-5 h-5 text-[#FFC04D]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Pending</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <PoundSterling className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-xs text-[#A1A1AA]">Total Payable</p>
                <p className="text-xl font-bold text-[#1ABC9C] font-['JetBrains_Mono']">{fmt(stats.totalPayable, user?.organisation_currency, { decimals: 0 })}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Header with Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Navigation */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrev}
            className="text-[#A1A1AA] hover:text-[#F2F2F2]"
            data-testid="prev-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h2
            className="min-w-[240px] text-center font-['Manrope'] tracking-tight tabular-nums select-none"
            data-testid="schedule-header-title"
          >
            <span className="text-2xl font-semibold text-[#F2F2F2]">
              {getTitleParts().range}
            </span>
            <span className="ml-2 text-base font-normal text-[#A1A1AA]">
              {getTitleParts().year}
            </span>
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNext}
            className="text-[#A1A1AA] hover:text-[#F2F2F2]"
            data-testid="next-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToday}
            className="ml-2 border-white/10 text-[#A1A1AA] hover:text-[#F2F2F2]"
            data-testid="today-btn"
          >
            Today
          </Button>
        </div>

        {/* View Toggle & Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* View Toggle - Week/Month */}
          <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
            <TabsList className="bg-[#1A1A1A] border-white/5">
              <TabsTrigger 
                value="week" 
                className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C] text-xs gap-1"
                data-testid="week-view-tab"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Week
              </TabsTrigger>
              <TabsTrigger 
                value="month" 
                className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C] text-xs gap-1"
                data-testid="month-view-tab"
              >
                <CalendarRange className="w-3.5 h-3.5" />
                Month
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Filter Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`border-white/10 ${hasActiveFilters ? 'text-[#1ABC9C] border-[#1ABC9C]/30' : 'text-[#A1A1AA]'}`}
            data-testid="filter-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-2 bg-[#1ABC9C]/20 text-[#1ABC9C] border-0 text-xs">Active</Badge>
            )}
          </Button>

          {/* Bulk Create Button */}
          <Button
            variant="outline"
            className="border-[#1ABC9C]/30 text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
            onClick={() => {
              setBulkPreview(null);
              setShowBulkModal(true);
            }}
            data-testid="bulk-create-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Bulk Create
          </Button>

          {/* Add Shift Button */}
          <Button
            className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
            onClick={() => {
              setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
              setNewShift(prev => ({ ...prev, date: format(new Date(), 'yyyy-MM-dd') }));
              setShowCreateModal(true);
            }}
            data-testid="create-shift-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Shift
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1 min-w-[150px]">
                <Label className="text-xs text-[#A1A1AA]">Medic</Label>
                <Select value={filters.medic_id} onValueChange={(v) => setFilters(f => ({ ...f, medic_id: v }))}>
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 h-9" data-testid="filter-medic">
                    <SelectValue placeholder="All medics" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2B2B2B] border-white/10">
                    <SelectItem value="all">All Medics</SelectItem>
                    <SelectItem value="unassigned">Unassigned Only</SelectItem>
                    {medics.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1 min-w-[130px]">
                <Label className="text-xs text-[#A1A1AA]">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 h-9" data-testid="filter-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2B2B2B] border-white/10">
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="standby">Standby</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1 min-w-[130px]">
                <Label className="text-xs text-[#A1A1AA]">Shift Type</Label>
                <Select value={filters.shift_type} onValueChange={(v) => setFilters(f => ({ ...f, shift_type: v }))}>
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 h-9" data-testid="filter-shift-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2B2B2B] border-white/10">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="night">Night</SelectItem>
                    <SelectItem value="standby">Standby</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 min-w-[150px]">
                <Label className="text-xs text-[#A1A1AA]">Job Type</Label>
                <Select value={filters.job_type_id} onValueChange={(v) => setFilters(f => ({ ...f, job_type_id: v }))}>
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 h-9" data-testid="filter-job-type">
                    <SelectValue placeholder="All job types" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2B2B2B] border-white/10">
                    <SelectItem value="all">All Job Types</SelectItem>
                    {jobTypes.map(jt => (
                      <SelectItem key={jt.id} value={jt.id}>{jt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1 flex-1 min-w-[150px]">
                <Label className="text-xs text-[#A1A1AA]">Location</Label>
                <Input
                  placeholder="Filter by location..."
                  value={filters.location}
                  onChange={(e) => setFilters(f => ({ ...f, location: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10 h-9"
                  data-testid="filter-location"
                />
              </div>
              
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-[#A1A1AA] hover:text-red-400"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No-matches-in-view banner */}
      {hasActiveFilters && filteredShifts.length === 0 && outOfRangeMatches.length > 0 && !bannerDismissed && (
        <Card
          className="bg-[#1A1A1A] border border-[#1ABC9C]/30"
          data-testid="filter-no-match-banner"
        >
          <CardContent className="p-4 flex items-start gap-3 flex-wrap">
            <div className="p-2 rounded-lg bg-[#1ABC9C]/15 mt-0.5">
              <Filter className="w-4 h-4 text-[#1ABC9C]" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <p className="text-sm font-medium text-[#F2F2F2]">
                No matches in {getDisplayTitle()}
              </p>
              <p className="text-xs text-[#A1A1AA] mt-0.5">
                {outOfRangeMatches.length} shift{outOfRangeMatches.length === 1 ? '' : 's'} match the current filter outside this view
                {filters.status !== 'all' && <> · <span className="text-[#1ABC9C]">status: {filters.status}</span></>}
                .
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={jumpToEarliestMatch}
                className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
                data-testid="jump-to-earliest-match-btn"
              >
                Jump to earliest match
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setBannerDismissed(true)}
                className="text-[#A1A1AA] hover:text-[#F2F2F2]"
                data-testid="dismiss-no-match-banner-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar View */}
      <ScheduleCalendar
        paddedDays={paddedDays}
        viewMode={viewMode}
        currentDate={currentDate}
        draggedShift={draggedShift}
        medics={medics}
        expandedShiftId={expandedShiftId}
        getShiftsForDay={getShiftsForDay}
        getLeaveForDay={getLeaveForDay}
        getShiftConflicts={getShiftConflicts}
        handleDayClick={handleDayClick}
        handleDragOver={handleDragOver}
        handleDrop={handleDrop}
        handleDragStart={handleDragStart}
        toggleShiftExpand={toggleShiftExpand}
        closeExpandedShift={closeExpandedShift}
        handleUpdateShift={handleUpdateShift}
        handleMedicAssignment={handleMedicAssignment}
        handleDeleteShift={handleDeleteShift}
        handleMarkNoShow={handleMarkNoShow}
      />

      {/* Legend */}
      <CalendarLegend />

      {/* Create Shift Modal */}
      <CreateShiftModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        newShift={newShift}
        setNewShift={setNewShift}
        onSave={handleCreateShift}
        jobTypes={jobTypes}
        contracts={contracts}
        medics={medics}
      />

      {/* Bulk Create Modal */}
      <BulkCreateModal
        open={showBulkModal}
        onOpenChange={setShowBulkModal}
        bulkData={bulkData}
        setBulkData={setBulkData}
        bulkPreview={bulkPreview}
        setBulkPreview={setBulkPreview}
        bulkLoading={bulkLoading}
        onPreview={handleBulkPreview}
        onCreate={handleBulkCreate}
        contracts={contracts}
        jobTypes={jobTypes}
      />

      {/* Compliance Warning Modal */}
      {showComplianceWarning && currentWarning && (
        <ComplianceWarningModal
          isOpen={showComplianceWarning}
          onClose={handleCancelComplianceWarning}
          onConfirm={handleConfirmComplianceWarning}
          warningType={currentWarning.type}
          medicName={currentWarning.medicName}
          jobTypeName={currentWarning.jobTypeName}
        />
      )}
    </div>
  );
}
